import {
  type Permission,
  type PrincipalRole,
  canReadTaxNfe,
  canUploadTaxNfe,
  permissionsForRole,
  principalRoleSchema,
} from "@backchannel/tax-contracts";
import type { MessageKey } from "~/i18n/messages/pt-BR";

export function parsePrincipalRole(value: string | undefined | null): PrincipalRole | null {
  const parsed = principalRoleSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function roleLabelKey(role: PrincipalRole): MessageKey {
  switch (role) {
    case "SUPER_ADMIN":
      return "settings.access.role.superAdmin";
    case "TAX_COMMERCIAL":
      return "settings.access.role.taxCommercial";
    case "TAX_MANAGER":
      return "settings.access.role.taxManager";
    case "TAX_READER":
      return "settings.access.role.taxReader";
    case "TAX_DEVELOPER":
      return "settings.access.role.taxDeveloper";
  }
}

export function viewAsShortKey(role: PrincipalRole): MessageKey {
  switch (role) {
    case "SUPER_ADMIN":
      return "nav.viewAs.own";
    case "TAX_COMMERCIAL":
      return "nav.viewAs.short.commercial";
    case "TAX_MANAGER":
      return "nav.viewAs.short.manager";
    case "TAX_READER":
      return "nav.viewAs.short.reader";
    case "TAX_DEVELOPER":
      return "nav.viewAs.short.developer";
  }
}

export function roleHintKey(role: PrincipalRole): MessageKey {
  switch (role) {
    case "SUPER_ADMIN":
      return "settings.access.superAdminRoleHint";
    case "TAX_COMMERCIAL":
      return "settings.access.commercialRoleHint";
    case "TAX_MANAGER":
      return "settings.access.managerRoleHint";
    case "TAX_READER":
      return "settings.access.readerRoleHint";
    case "TAX_DEVELOPER":
      return "settings.access.developerRoleHint";
  }
}

/**
 * Super-admin "view as" is a UI lens. The signed-in principal is unchanged.
 * Intersection drops ADMIN so `hasPermission` cannot treat the preview as a
 * superset of every other capability.
 */
export function permissionsForPreview(
  held: readonly Permission[],
  previewRole: PrincipalRole | null,
): Permission[] {
  if (!previewRole || previewRole === "SUPER_ADMIN") return [...held];
  return permissionsForRole(previewRole).filter(
    (permission) =>
      held.includes(permission) ||
      held.includes("ADMIN:TAX_PLATFORM:ALL") ||
      (permission === "READ:TAX_NFE:ALL" && canReadTaxNfe(held)) ||
      (permission === "CREATE:TAX_NFE:ALL" && canUploadTaxNfe(held)),
  );
}
